import { ChangeDetectionStrategy, Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faArrowRight,
  faBars,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircleHalfStroke,
  faClose,
  faEllipsisVertical,
  faPlus,
  faSquare,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { GALAXY_SETTINGS, GalaxyInjectorKeys, GalaxySettings, ignorePromise, MenuItem } from '@ebcont/ebcont.galaxy.js';
import { TranslateService } from '@ngx-translate/core';
import { AuthServiceInterface } from '@core/auth/auth.interface';
import { AUTH_SERVICE } from '@core/auth/token';

import { ApplicationConfigurationService } from '@core/services/configuration/application.configuration.service';
import {Router} from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { map, tap, startWith, switchMap, shareReplay, catchError } from 'rxjs/operators';
import { UserService } from '@core/services/user/user.service';
import { Observable, of } from 'rxjs';

type UserState = {
  email: string | null;
  fullName: string | null;
  isAdmin: boolean;
};

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('opacityScaleDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(.95)' }),
        animate('100ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate('75ms ease-in', style({ opacity: 0, transform: 'scale(.95)' }))
      ])
    ]),
    trigger('opacity', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms ease', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('300ms ease', style({ opacity: 0 }))])
    ]),
    trigger('opacityScaleUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(1.1)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate('150ms ease-in', style({ opacity: 0, transform: 'scale(1.1)' }))
      ])
    ]),
    trigger('translateX', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-in-out', style({ transform: 'translateX(0)' }))
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)' }),
        animate('300ms ease-in-out', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ],
  standalone: false
})
export class AppComponent implements OnInit {
  isOffCanvasMenu = false;
  isOffCanvasMenuDialog = false;
  public user$!: Observable<UserState | null>;
  public menuItems$!: Observable<MenuItem[]>;

  constructor(
    private userService: UserService,
    private library: FaIconLibrary,
    private translate: TranslateService,
    @Inject(GalaxyInjectorKeys.SETTINGS) private settings: GalaxySettings,
    @Inject(AUTH_SERVICE) private authService: AuthServiceInterface,
    public applicationConfigurationService: ApplicationConfigurationService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    library.addIcons(
      faBars,
      faCircleHalfStroke,
      faSquare,
      faTrash,
      faArrowRight,
      faClose,
      faPlus,
      faEllipsisVertical,
      faChevronLeft,
      faChevronRight,
      faChevronDown
    );
  }

  ngOnInit(): void {
    this.oidcAppComponent();
    const lang = this.settings.get(GALAXY_SETTINGS.LANGUAGE, 'en');
    this.translate.use(lang);
  }

  toggleOffCanvasMenu(force?: boolean) {
    this.isOffCanvasMenu = force ?? !this.isOffCanvasMenu;

    if (this.isOffCanvasMenuDialog) {
      setTimeout(() => {
        this.isOffCanvasMenuDialog = !this.isOffCanvasMenuDialog;
      }, 400);
    } else {
      this.isOffCanvasMenuDialog = !this.isOffCanvasMenuDialog;
    }
  }

  oidcAppComponent(): void {
    const initialAuthStatus = this.authService.isAuthenticated();
    if (initialAuthStatus) {
      const initialEmail = this.authService.getUserEmail();
    }
    const isAuthenticated$ = this.authService.getLoginSuccessSubject().pipe(
      startWith(initialAuthStatus),
      tap(isAuthenticated => {
        if (!isAuthenticated) { this.authService.login(); }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const baseUser$ = isAuthenticated$.pipe(
      map(isAuthenticated => {
        console.log('[AppComponent] base user...');
        if (!isAuthenticated) {
          return null;
        }
        return {
          email: this.authService.getUserEmail() ?? null,
          fullName: this.authService.getUserFullName() ?? 'n/a',
          isAdmin: false
        };
      })
    );

    this.user$ = baseUser$.pipe(
      switchMap(user => {
        if (!user) {
          return of(null);
        }
        return this.userService.getAdminStatus().pipe(
          map(isAdmin => ({ ...user, isAdmin: isAdmin })),
          catchError(error => {
            console.error('[AppComponent] Could not get admin status. Defaulting to non-admin.', error);
            return of(user);
          }),
        );
      }),
      tap(user => {
          console.log('[AppComponent] tap user: ', user);
          this.cdr.markForCheck();
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.menuItems$ = this.user$.pipe(
      map(user => {
        if (!user || !user.email) {
          return [];
        }
        return [
          {
            label: 'profile.singular',
            id: 'profile',
            isRouterLink: false,
            clickEvent: () => {
              ignorePromise(
                this.router.navigate(['/team'], {
                  queryParams: {
                    employee: user.email,
                    searchValue: user.email,
                    tab: 'profile'
                  }
                })
              );
            }
          },
          {
            label: 'header.menu.logout',
            id: 'logout',
            isRouterLink: false,
            clickEvent: () => {
              this.authService.logout();
            }
          }
        ];
      })
    );
  }
}
